/**
 * CallFlow Service
 *
 * Provides methods to interact with CallFlow:
 * - Direct Supabase queries (CRUD operations with RLS)
 * - FastAPI endpoints (execution control)
 */

import { supabase } from '../supabase/supabase';

const FASTAPI_BASE_URL = import.meta.env.VITE_CALL_SERVICE_URL || 'http://localhost:8000';

// ============================================================================
// Types
// ============================================================================

export interface CallFlow {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  assistant_id: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'completed_with_failures' | 'cancelled';
  retry_delays: number[];
  variables?: Record<string, any>;
  max_calls_per_second_per_number: number;
  max_concurrent_calls: number;
  total_leads: number;
  calls_completed: number;
  calls_failed: number;
  calls_in_progress: number;
  created_at: string;
  updated_at: string;
}

export interface CallFlowLead {
  id: string;
  callflow_id: string;
  lead_id: string;
  status: 'pending' | 'calling' | 'completed' | 'failed';
  attempts: number;
  next_attempt_at?: string;
  last_ended_reason?: string;
  last_call_id?: string;
  last_attempt_at?: string;
  attempt_history: Array<{
    attempt: number;
    timestamp: string;
    ended_reason: string;
    call_log_id?: string;
    error_details?: string;
    delay_before_next?: number;
  }>;
  added_at: string;
  leads?: {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
    phone_number: string;
  };
}

export interface CallFlowPhoneNumber {
  id: string;
  callflow_id: string;
  phone_number_id: string;
  is_active: boolean;
  last_used_at?: string;
  total_calls: number;
  successful_calls: number;
  failed_calls: number;
  created_at: string;
  phone_numbers?: {
    id: string;
    phone_number: string;
    type: string;
  };
}

export interface CallFlowStats {
  callflow_id: string;
  status: string;
  total_leads: number;
  calls_completed: number;
  calls_failed: number;
  calls_in_progress: number;
  completion_percentage: number;
  is_running: boolean;
  worker_id?: string;
  started_at?: string;
  last_heartbeat?: string;
}

export interface CallFlowExecutionLog {
  id: string;
  callflow_id: string;
  event_type: string;
  message?: string;
  details?: Record<string, any>;
  created_at: string;
  callflow_lead_id?: string;
  call_log_id?: string;
  phone_number_id?: string;
}

export interface CreateCallFlowData {
  name: string;
  description?: string;
  assistant_id: string;
  retry_delays?: number[];
  variables?: Record<string, any>;
}

export interface UpdateCallFlowData {
  name?: string;
  description?: string;
  retry_delays?: number[];
  variables?: Record<string, any>;
}

// ============================================================================
// Helper Functions
// ============================================================================

async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

async function makeFastAPIRequest(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  const url = `${FASTAPI_BASE_URL}${path}`;

  return fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
}

// ============================================================================
// CallFlow CRUD (Direct Supabase Queries)
// ============================================================================

export const callFlowService = {
  /**
   * Create a new CallFlow
   */
  async createCallFlow(organizationId: string, data: CreateCallFlowData): Promise<CallFlow> {
    // Client-side validation
    if (!data.name?.trim()) {
      throw new Error('Name is required');
    }
    if (!data.assistant_id) {
      throw new Error('Assistant ID is required');
    }
    if (data.retry_delays && !Array.isArray(data.retry_delays)) {
      throw new Error('retry_delays must be an array of integers');
    }

    const { data: callflow, error } = await supabase
      .from('callflows')
      .insert({
        organization_id: organizationId,
        name: data.name,
        description: data.description,
        assistant_id: data.assistant_id,
        retry_delays: data.retry_delays || [2, 10, 30],
        variables: data.variables || {},
        status: 'draft',
      })
      .select()
      .single();

    if (error) {
      console.error('Failed to create callflow:', error);
      throw new Error(error.message || 'Failed to create CallFlow');
    }

    return callflow;
  },

  /**
   * List CallFlows with optional filters
   */
  async listCallFlows(organizationId: string, filters?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ callflows: CallFlow[]; total: number }> {
    const { limit = 100, offset = 0, status } = filters || {};

    let query = supabase
      .from('callflows')
      .select('*, assistants(name)', { count: 'exact' })
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Failed to list callflows:', error);
      throw new Error(error.message || 'Failed to list CallFlows');
    }

    return {
      callflows: data || [],
      total: count || 0,
    };
  },

  /**
   * Get a single CallFlow by ID
   */
  async getCallFlow(organizationId: string, id: string): Promise<CallFlow> {
    const { data, error } = await supabase
      .from('callflows')
      .select(`
        *,
        assistants(name, id),
        callflow_leads(count),
        callflow_phone_numbers(count)
      `)
      .eq('id', id)
      .eq('organization_id', organizationId)
      .single();

    if (error) {
      console.error('Failed to get callflow:', error);

      if (error.code === 'PGRST116') {
        throw new Error('CallFlow not found');
      }

      throw new Error(error.message || 'Failed to get CallFlow');
    }

    return data;
  },

  /**
   * Update a CallFlow
   */
  async updateCallFlow(organizationId: string, id: string, updates: UpdateCallFlowData): Promise<CallFlow> {
    // Validate retry_delays if provided
    if (updates.retry_delays && !Array.isArray(updates.retry_delays)) {
      throw new Error('retry_delays must be an array of integers');
    }

    // Note: Database trigger will prevent certain updates when active
    const { data, error } = await supabase
      .from('callflows')
      .update(updates)
      .eq('id', id)
      .eq('organization_id', organizationId)
      .select()
      .single();

    if (error) {
      console.error('Failed to update callflow:', error);
      throw new Error(error.message || 'Failed to update CallFlow');
    }

    return data;
  },

  /**
   * Delete a CallFlow
   */
  async deleteCallFlow(organizationId: string, id: string): Promise<void> {
    // Note: Database trigger will prevent deletion of active CallFlows
    const { error } = await supabase
      .from('callflows')
      .delete()
      .eq('id', id)
      .eq('organization_id', organizationId);

    if (error) {
      console.error('Failed to delete callflow:', error);
      throw new Error(error.message || 'Failed to delete CallFlow');
    }
  },

  // ============================================================================
  // CallFlow Leads (Direct Supabase Queries)
  // ============================================================================

  /**
   * Add leads to a CallFlow
   */
  async addLeads(organizationId: string, callflowId: string, leadIds: string[]): Promise<{ added_count: number }> {
    if (!Array.isArray(leadIds) || leadIds.length === 0) {
      throw new Error('lead_ids must be a non-empty array');
    }

    // Verify CallFlow exists and belongs to organization
    const { data: callflow, error: cfError } = await supabase
      .from('callflows')
      .select('id')
      .eq('id', callflowId)
      .eq('organization_id', organizationId)
      .single();

    if (cfError || !callflow) {
      throw new Error('CallFlow not found');
    }

    // Prepare records to insert
    const records = leadIds.map(leadId => ({
      callflow_id: callflowId,
      lead_id: leadId,
      status: 'pending' as const,
    }));

    // Insert with conflict handling (ignore duplicates)
    const { data: addedLeads, error } = await supabase
      .from('callflow_leads')
      .upsert(records, { onConflict: 'callflow_id,lead_id', ignoreDuplicates: true })
      .select();

    if (error) {
      console.error('Failed to add leads to callflow:', error);
      throw new Error(error.message || 'Failed to add leads');
    }

    return {
      added_count: addedLeads?.length || 0,
    };
  },

  /**
   * Remove a lead from a CallFlow
   */
  async removeLead(organizationId: string, callflowId: string, leadId: string): Promise<void> {
    // Verify CallFlow belongs to organization
    const { data: callflow } = await supabase
      .from('callflows')
      .select('id, status')
      .eq('id', callflowId)
      .eq('organization_id', organizationId)
      .single();

    if (!callflow) {
      throw new Error('CallFlow not found');
    }

    // Note: Database trigger will prevent removal from active CallFlow
    // and removal of calling/completed leads
    const { error } = await supabase
      .from('callflow_leads')
      .delete()
      .eq('callflow_id', callflowId)
      .eq('lead_id', leadId);

    if (error) {
      console.error('Failed to remove lead from callflow:', error);
      throw new Error(error.message || 'Failed to remove lead');
    }
  },

  /**
   * List leads in a CallFlow
   */
  async listLeads(
    organizationId: string,
    callflowId: string,
    filters?: {
      status?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{
    leads: CallFlowLead[];
    summary: {
      total: number;
      pending: number;
      calling: number;
      completed: number;
      failed: number;
    };
  }> {
    // Verify CallFlow belongs to organization
    const { data: callflow } = await supabase
      .from('callflows')
      .select('id')
      .eq('id', callflowId)
      .eq('organization_id', organizationId)
      .single();

    if (!callflow) {
      throw new Error('CallFlow not found');
    }

    const { limit = 100, offset = 0, status } = filters || {};

    let query = supabase
      .from('callflow_leads')
      .select(`
        *,
        leads(id, first_name, last_name, email, phone_number)
      `)
      .eq('callflow_id', callflowId)
      .order('added_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (status) {
      query = query.eq('status', status);
    }

    const { data: leads, error } = await query;

    if (error) {
      console.error('Failed to list callflow leads:', error);
      throw new Error(error.message || 'Failed to list leads');
    }

    // Count by status
    const { data: statusCounts } = await supabase
      .from('callflow_leads')
      .select('status')
      .eq('callflow_id', callflowId);

    const summary = {
      total: statusCounts?.length || 0,
      pending: statusCounts?.filter(l => l.status === 'pending').length || 0,
      calling: statusCounts?.filter(l => l.status === 'calling').length || 0,
      completed: statusCounts?.filter(l => l.status === 'completed').length || 0,
      failed: statusCounts?.filter(l => l.status === 'failed').length || 0,
    };

    return { leads: leads || [], summary };
  },

  // ============================================================================
  // CallFlow Phone Numbers (Direct Supabase Queries)
  // ============================================================================

  /**
   * Add phone numbers to a CallFlow
   */
  async addPhoneNumbers(
    organizationId: string,
    callflowId: string,
    phoneNumberIds: string[]
  ): Promise<{ added_count: number }> {
    if (!Array.isArray(phoneNumberIds) || phoneNumberIds.length === 0) {
      throw new Error('phone_number_ids must be a non-empty array');
    }

    // Verify CallFlow exists and belongs to organization
    const { data: callflow, error: cfError } = await supabase
      .from('callflows')
      .select('id')
      .eq('id', callflowId)
      .eq('organization_id', organizationId)
      .single();

    if (cfError || !callflow) {
      throw new Error('CallFlow not found');
    }

    // Prepare records to insert
    const records = phoneNumberIds.map(phoneNumberId => ({
      callflow_id: callflowId,
      phone_number_id: phoneNumberId,
      is_active: true,
    }));

    // Insert with conflict handling (ignore duplicates)
    const { data: addedPhones, error } = await supabase
      .from('callflow_phone_numbers')
      .upsert(records, { onConflict: 'callflow_id,phone_number_id', ignoreDuplicates: true })
      .select();

    if (error) {
      console.error('Failed to add phone numbers to callflow:', error);
      throw new Error(error.message || 'Failed to add phone numbers');
    }

    return {
      added_count: addedPhones?.length || 0,
    };
  },

  /**
   * Remove (deactivate) a phone number from a CallFlow
   */
  async removePhoneNumber(organizationId: string, callflowId: string, phoneNumberId: string): Promise<void> {
    // Verify CallFlow belongs to organization
    const { data: callflow } = await supabase
      .from('callflows')
      .select('id')
      .eq('id', callflowId)
      .eq('organization_id', organizationId)
      .single();

    if (!callflow) {
      throw new Error('CallFlow not found');
    }

    // Soft delete - set is_active to false
    const { error } = await supabase
      .from('callflow_phone_numbers')
      .update({ is_active: false })
      .eq('callflow_id', callflowId)
      .eq('phone_number_id', phoneNumberId);

    if (error) {
      console.error('Failed to remove phone number from callflow:', error);
      throw new Error(error.message || 'Failed to remove phone number');
    }
  },

  /**
   * List phone numbers in a CallFlow
   */
  async listPhoneNumbers(organizationId: string, callflowId: string): Promise<{
    phone_numbers: CallFlowPhoneNumber[];
    summary: {
      total: number;
      active: number;
      inactive: number;
      total_calls: number;
      successful_calls: number;
    };
  }> {
    // Verify CallFlow belongs to organization
    const { data: callflow } = await supabase
      .from('callflows')
      .select('id')
      .eq('id', callflowId)
      .eq('organization_id', organizationId)
      .single();

    if (!callflow) {
      throw new Error('CallFlow not found');
    }

    const { data: phoneNumbers, error } = await supabase
      .from('callflow_phone_numbers')
      .select(`
        *,
        phone_numbers(id, phone_number, type)
      `)
      .eq('callflow_id', callflowId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Failed to list callflow phone numbers:', error);
      throw new Error(error.message || 'Failed to list phone numbers');
    }

    const summary = {
      total: phoneNumbers?.length || 0,
      active: phoneNumbers?.filter(p => p.is_active).length || 0,
      inactive: phoneNumbers?.filter(p => !p.is_active).length || 0,
      total_calls: phoneNumbers?.reduce((sum, p) => sum + (p.total_calls || 0), 0) || 0,
      successful_calls: phoneNumbers?.reduce((sum, p) => sum + (p.successful_calls || 0), 0) || 0,
    };

    return { phone_numbers: phoneNumbers || [], summary };
  },

  // ============================================================================
  // CallFlow Execution Control (FastAPI)
  // ============================================================================

  /**
   * Start CallFlow execution
   */
  async startCallFlow(callflowId: string): Promise<{ status: string; message: string }> {
    const response = await makeFastAPIRequest(`/api/callflows/${callflowId}/start`, {
      method: 'POST',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to start CallFlow');
    }

    return response.json();
  },

  /**
   * Pause CallFlow execution
   */
  async pauseCallFlow(callflowId: string): Promise<{ status: string; message: string }> {
    const response = await makeFastAPIRequest(`/api/callflows/${callflowId}/pause`, {
      method: 'POST',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to pause CallFlow');
    }

    return response.json();
  },

  /**
   * Resume CallFlow execution
   */
  async resumeCallFlow(callflowId: string): Promise<{ status: string; message: string }> {
    const response = await makeFastAPIRequest(`/api/callflows/${callflowId}/resume`, {
      method: 'POST',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to resume CallFlow');
    }

    return response.json();
  },

  /**
   * Get CallFlow statistics
   */
  async getStats(callflowId: string): Promise<CallFlowStats> {
    const response = await makeFastAPIRequest(`/api/callflows/${callflowId}/stats`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get CallFlow stats');
    }

    return response.json();
  },

  /**
   * Get CallFlow execution logs
   */
  async getLogs(
    callflowId: string,
    filters?: {
      event_type?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{ logs: CallFlowExecutionLog[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.event_type) params.append('event_type', filters.event_type);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const path = params.toString()
      ? `/api/callflows/${callflowId}/logs?${params}`
      : `/api/callflows/${callflowId}/logs`;

    const response = await makeFastAPIRequest(path);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get CallFlow logs');
    }

    return response.json();
  },
};
